import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Menu, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material'
import MoreVertIcon       from '@mui/icons-material/MoreVert'
import PersonAddIcon      from '@mui/icons-material/PersonAdd'
import UpgradeIcon        from '@mui/icons-material/Upgrade'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import DeleteIcon         from '@mui/icons-material/DeleteOutlined'
import { supabase }       from '../../../supabaseClient'
import { readFunctionError } from '../../../lib/functionError'
import { LifecycleState } from '../../../types'
import PromoteDialog      from '../../../components/students/PromoteDialog'
import NewTrialDialog     from '../../../components/students/NewTrialDialog'

interface TrialRow {
  user_id:          string
  student_id:       string
  email:            string
  first_name:       string
  last_name:        string
  grade:            number | null
  school:           string | null
  lifecycle_state:  LifecycleState
  trial_started_at: string | null
  created_at:       string
}

const FILTER_LABELS = {
  active:   'Active',
  expired:  'Expired',
  promoted: 'Recently promoted',
} as const

type Filter = keyof typeof FILTER_LABELS

function daysSince(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
}

function daysRemaining(trialStartedAt: string | null): number | null {
  if (!trialStartedAt) return null
  const elapsed = daysSince(trialStartedAt) ?? 0
  return Math.max(0, 60 - elapsed)
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ManagerTrialsPage() {
  const [rows,           setRows]           = useState<TrialRow[]>([])
  const [filter,         setFilter]         = useState<Filter>('active')
  const [search,         setSearch]         = useState('')
  const [loading,        setLoading]        = useState(true)
  const [error,          setError]          = useState<string | null>(null)
  const [menuAnchor,     setMenuAnchor]     = useState<null | HTMLElement>(null)
  const [menuRow,        setMenuRow]        = useState<TrialRow | null>(null)
  const [promoteRow,     setPromoteRow]     = useState<TrialRow | null>(null)
  const [confirmExpire,  setConfirmExpire]  = useState<TrialRow | null>(null)
  const [confirmDelete,  setConfirmDelete]  = useState<TrialRow | null>(null)
  const [actioning,      setActioning]      = useState<string | null>(null)
  const [newTrialOpen,   setNewTrialOpen]   = useState(false)

  async function load() {
    setLoading(true)
    // Pull all student profiles whose lifecycle_state begins with 'trial_'
    // OR was promoted in the last 30 days (so the manager can verify).
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString()
    const states: LifecycleState[] = ['trial_active', 'trial_expired']
    const [{ data: trialProfiles }, { data: promotedEvents }] = await Promise.all([
      supabase
        .from('student_profile')
        .select('student_id, user_id, grade, school_name, lifecycle_state, trial_started_at, created_at')
        .in('lifecycle_state', states),
      supabase
        .from('student_lifecycle_event')
        .select('student_id, created_at')
        .eq('to_state', 'enrolled_active')
        .gte('created_at', cutoff)
        .order('created_at', { ascending: false }),
    ])

    const promotedIds = new Set((promotedEvents ?? []).map(e => e.student_id))
    let promotedProfiles: any[] = []
    if (promotedIds.size > 0) {
      const { data } = await supabase
        .from('student_profile')
        .select('student_id, user_id, grade, school_name, lifecycle_state, trial_started_at, created_at')
        .in('student_id', Array.from(promotedIds))
        .eq('lifecycle_state', 'enrolled_active')
      promotedProfiles = data ?? []
    }

    const allProfiles = [...(trialProfiles ?? []), ...promotedProfiles]
    if (allProfiles.length === 0) {
      setRows([]); setLoading(false); return
    }

    const userIds = allProfiles.map(p => p.user_id)
    const { data: users } = await supabase
      .from('users')
      .select('user_id, email, first_name, last_name')
      .in('user_id', userIds)
    const userMap: Record<string, any> = Object.fromEntries((users ?? []).map(u => [u.user_id, u]))

    const merged: TrialRow[] = allProfiles.map(p => ({
      user_id:          p.user_id,
      student_id:       p.student_id,
      email:            userMap[p.user_id]?.email      ?? '',
      first_name:       userMap[p.user_id]?.first_name ?? '',
      last_name:        userMap[p.user_id]?.last_name  ?? '',
      grade:            p.grade,
      school:           p.school_name,
      lifecycle_state:  p.lifecycle_state,
      trial_started_at: p.trial_started_at,
      created_at:       p.created_at,
    }))
    setRows(merged.sort((a, b) => (b.created_at).localeCompare(a.created_at)))
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const visible = rows.filter(r => {
    if (filter === 'active'   && r.lifecycle_state !== 'trial_active')   return false
    if (filter === 'expired'  && r.lifecycle_state !== 'trial_expired')  return false
    if (filter === 'promoted' && r.lifecycle_state !== 'enrolled_active') return false
    if (search) {
      const q = search.toLowerCase()
      if (!r.email.toLowerCase().includes(q) &&
          !r.first_name.toLowerCase().includes(q) &&
          !r.last_name.toLowerCase().includes(q) &&
          !(r.school ?? '').toLowerCase().includes(q)) return false
    }
    return true
  })

  function openMenu(e: React.MouseEvent<HTMLElement>, row: TrialRow) {
    setMenuAnchor(e.currentTarget); setMenuRow(row)
  }
  function closeMenu() {
    setMenuAnchor(null); setMenuRow(null)
  }

  async function doExpire(row: TrialRow) {
    setActioning(row.student_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('mark-trial-expired', {
      body: { student_id: row.student_id },
    })
    setActioning(null)
    setConfirmExpire(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  async function doDelete(row: TrialRow) {
    setActioning(row.user_id)
    const { error: invokeErr } = await supabase.functions.invoke('delete-user', {
      body: { user_id: row.user_id },
    })
    setActioning(null)
    setConfirmDelete(null)
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
    } else {
      setRows(prev => prev.filter(r => r.user_id !== row.user_id))
    }
  }

  function nameOrEmail(r: TrialRow) {
    const name = `${r.first_name} ${r.last_name}`.trim()
    return name || r.email
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1100 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Trials</Typography>
          <Typography variant="body2" color="text.secondary">
            Trial students are limited to Phase 1 for 60 days. Promote to full enrollment to unlock the rest of the app.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setNewTrialOpen(true)}
        >
          New trial
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          exclusive size="small" value={filter}
          onChange={(_, v) => v && setFilter(v)}
        >
          {(Object.keys(FILTER_LABELS) as Filter[]).map(f => (
            <ToggleButton key={f} value={f} sx={{ textTransform: 'none' }}>
              {FILTER_LABELS[f]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small" placeholder="Search by name, email, school…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260, ml: 'auto' }}
        />
      </Box>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
          {filter === 'active'   ? 'No active trials.'
         : filter === 'expired'  ? 'No expired trials.'
         : 'Nothing recently promoted.'}
        </Typography>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Started</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(r => {
                const remaining = daysRemaining(r.trial_started_at)
                const expiringSoon = r.lifecycle_state === 'trial_active' && remaining !== null && remaining < 14
                return (
                  <TableRow key={r.student_id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {nameOrEmail(r)}
                    </TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.grade ?? '—'}</TableCell>
                    <TableCell>{r.school ?? '—'}</TableCell>
                    <TableCell>
                      {r.lifecycle_state === 'trial_active' && (
                        <Chip
                          size="small"
                          color={expiringSoon ? 'warning' : 'primary'}
                          label={remaining !== null ? `Trial — ${remaining}d left` : 'Trial — active'}
                        />
                      )}
                      {r.lifecycle_state === 'trial_expired' && (
                        <Chip size="small" color="default" label="Expired" />
                      )}
                      {r.lifecycle_state === 'enrolled_active' && (
                        <Chip size="small" color="success" label="Promoted" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(r.trial_started_at ?? r.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={e => openMenu(e, r)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuRow && (menuRow.lifecycle_state === 'trial_active' || menuRow.lifecycle_state === 'trial_expired') && (
          <MenuItem onClick={() => { setPromoteRow(menuRow); closeMenu() }}>
            <UpgradeIcon fontSize="small" sx={{ mr: 1.5 }} /> Promote to Enrolled
          </MenuItem>
        )}
        {menuRow?.lifecycle_state === 'trial_active' && (
          <MenuItem onClick={() => { setConfirmExpire(menuRow); closeMenu() }}>
            <HourglassBottomIcon fontSize="small" sx={{ mr: 1.5 }} /> Mark Expired
          </MenuItem>
        )}
        <MenuItem onClick={() => { setConfirmDelete(menuRow); closeMenu() }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete account
        </MenuItem>
      </Menu>

      <PromoteDialog
        open={!!promoteRow}
        studentId={promoteRow?.student_id ?? null}
        studentLabel={promoteRow ? `${nameOrEmail(promoteRow)} (${promoteRow.email})` : ''}
        fromState={promoteRow?.lifecycle_state === 'trial_expired' ? 'trial_expired' : 'trial_active'}
        onClose={() => setPromoteRow(null)}
        onPromoted={load}
      />

      <NewTrialDialog
        open={newTrialOpen}
        onClose={() => setNewTrialOpen(false)}
        onCreated={load}
      />

      <Dialog open={!!confirmExpire} onClose={() => setConfirmExpire(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Mark trial as expired?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmExpire ? nameOrEmail(confirmExpire) : ''}</strong> will lose access
            to new Phase 1 actions. They can still view their existing work.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmExpire(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => confirmExpire && doExpire(confirmExpire)}
            disabled={actioning === confirmExpire?.student_id}
          >
            {actioning === confirmExpire?.student_id ? <CircularProgress size={18} /> : 'Mark Expired'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete account?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{confirmDelete ? nameOrEmail(confirmDelete) : ''}</strong> and
            all associated data. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={() => confirmDelete && doDelete(confirmDelete)}
            disabled={actioning === confirmDelete?.user_id}
          >
            {actioning === confirmDelete?.user_id ? <CircularProgress size={18} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}
